import Bugsnag from "@bugsnag/js";
import { ContentType } from "@utility/constants";

const getUserAvatarEdits = (user, edits) => {
  if (edits && Object.keys(edits).length) {
    return edits;
  }

  if (!user) {
    return {};
  }

  const { avatarTop, avatarLeft, avatarHeight, avatarWidth, avatarRotation } =
    user;

  if (!avatarHeight || !avatarWidth) {
    return {};
  }

  return {
    rotate: avatarRotation,
    extract: {
      left: avatarLeft,
      top: avatarTop,
      width: avatarWidth,
      height: avatarHeight,
    },
  };
};

export const generateUserAvatarUrl = ({ user, edits = {} } = {}) => {
  let url;

  if (
    user.avatarContentType !== undefined &&
    user.avatarContentType !== "" &&
    user.avatarContentType !== "NONE" &&
    user.avatarBucketName !== "" &&
    user.avatarFileName !== ""
  ) {
    let imageRequest;
    let error;
    switch (user.avatarContentType) {
      case ContentType.IMAGE:
        imageRequest = JSON.stringify({
          bucket: user.avatarBucketName,
          key: user.avatarFileName,
          edits: getUserAvatarEdits(user, edits),
        });
        url = `https://${
          process.env.NEXT_PUBLIC_CLOUDFRONT_POST_IMAGES_DISTRIBUTION_DOMAIN
        }/${Buffer.from(imageRequest).toString("base64")}`;
        break;
      case ContentType.VIDEO:
        url = `https://${process.env.NEXT_PUBLIC_CLOUDFRONT_AVATARS_DISTRIBUTION_DOMAIN}/${user.avatarFileName}`;
        break;
      case ContentType.DRAWING:
        url = `https://${process.env.NEXT_PUBLIC_CLOUDFRONT_POST_DRAWING_DISTRIBUTION_DOMAIN}/${user.avatarFileName}`;
        break;
      default:
        error = new Error(
          `Invalid avatarContentType ${user.avatarContentType} for user ${user.id}`
        );
        Bugsnag.notify(error, (report) => {
          // eslint-disable-next-line no-param-reassign
          report.context = `generateUserAvatarUrl - Received ${user.avatarContentType}`;
          report.addMetadata("state", { id: user.id });
        });
    }
  }
  return url;
};

export const generatePostImageUrl = ({ post, edits = {} } = {}) => {
  if (post.contentUrl) {
    return post.contentUrl;
  }
  if (post.contentType !== ContentType.IMAGE) {
    const error = new Error(
      `Invalid generatePostImageUrl content type ${post.contentType} for postId ${post.id}`
    );
    Bugsnag.notify(error, (report) => {
      // eslint-disable-next-line no-param-reassign
      report.context = `generatePostImageUrl - Received ${post.contentType}`;
      report.addMetadata("state", { id: post.id });
    });
    return "";
  }
  const imageRequest = JSON.stringify({
    bucket: post.contentBucketName,
    key: post.contentFileName,
    edits,
  });
  const url = `https://${
    process.env.NEXT_PUBLIC_CLOUDFRONT_POST_IMAGES_DISTRIBUTION_DOMAIN
  }/${Buffer.from(imageRequest).toString("base64")}`;
  return url;
};

export const generatePostVideoUrl = ({ post }) => {
  if (post.contentUrl) {
    return post.contentUrl;
  }
  if (post.contentType !== ContentType.VIDEO) {
    const error = new Error(
      `Invalid generatePostVideoUrl image type: ${post.contentType}`
    );
    Bugsnag.notify(error, (report) => {
      // eslint-disable-next-line no-param-reassign
      report.context = `generatePostVideoUrl - Received ${post.contentType}`;
      report.addMetadata("state", { id: post.id });
    });
    return "";
  }
  if (post.contentFileName !== "") {
    return `https://${process.env.NEXT_PUBLIC_CLOUDFRONT_POST_VIDEOS_DISTRIBUTION_DOMAIN}/${post.contentFileName}`;
  }
  return "";
};

export const generatePostImageThumbUrl = ({ post, edits = {} } = {}) => {
  if (post.thumbnailUrl) {
    return post.thumbnailUrl;
  }
  return generatePostImageUrl({ post, edits });
};

export const generatePostVideoThumbnailUrl = ({ post }) => {
  if (post.thumbnailUrl) {
    return post.thumbnailUrl;
  }
  if (post.contentType !== ContentType.VIDEO) {
    const error = new Error(
      `Invalid generatePostVideoThumbnailUrl content type: ${post.contentType}`
    );
    Bugsnag.notify(error, (report) => {
      const reportVar = report;
      reportVar.context = `generatePostVideoThumbnailUrl - Received ${post.contentType}`;
      reportVar.addMetadata("state", { id: post.id });
    });
    return "";
  }
  if (post.thumbnailFileName !== "") {
    return `https://${process.env.NEXT_PUBLIC_CLOUDFRONT_POST_VIDEO_THUMBNAILS_DISTRIBUTION_DOMAIN}/${post.thumbnailFileName}`;
  }
  return "";
};

export const generatePostDrawingUrl = ({ post }) => {
  // console.log("POST: ", JSON.stringify(post, null, 4));
  if (post.contentUrl) {
    return post.contentUrl;
  }

  if (post.contentFileName !== "") {
    return `https://${process.env.NEXT_PUBLIC_CLOUDFRONT_POST_DRAWING_DISTRIBUTION_DOMAIN}/${post.contentFileName}`;
  }

  return "";
};

export const abbreviateNumber = (num, fixed) => {
  if (typeof num !== "number") {
    return null;
  } // terminate early
  if (num === 0) {
    return "0";
  } // terminate early
  const precision = !fixed || fixed < 0 ? 0 : fixed; // number of decimal places to show
  const b = num.toPrecision(2).split("e"); // get power
  const k = b.length === 1 ? 0 : Math.floor(Math.min(b[1].slice(1), 14) / 3); // floor at decimals, ceiling at trillions
  const c =
    k < 1
      ? num.toFixed(0 + precision)
      : (num / 10 ** (k * 3)).toFixed(1 + precision); // divide by power
  const d = c < 0 ? c : Math.abs(c); // enforce -0 is 0
  const e = d + ["", "K", "M", "B", "T"][k]; // append power
  if (e === "NaN") {
    return null;
  }
  return e;
};

export const calculateFontSizeForTextContent = ({ textLength, maxFontSize }) =>
  Math.max(40 - textLength * 0.4, maxFontSize);
